import React from 'react';
import moment from 'moment';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { InfoTooltip } from '@components/InfoTooltip';
import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import lateEarlyAlertsService from '@/services/lateEarlyAlerts';

// .card is shared from the parent page's module — see LateEarlyLoginAlerts.module.scss.
import pageStyles from '../LateEarlyLoginAlerts.module.scss';
import styles from './CheckInCharts.module.scss';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Semibold}
        color="gray-700"
      >
        {label}
      </Text>
      {payload.map((entry) => (
        <div key={entry.dataKey} className={styles.tooltipRow}>
          <span
            className={styles.tooltipSwatch}
            style={{ background: entry.fill || entry.color }}
          />
          <Text variant={textVariantsEnum.Text_xs} color="gray-600">
            {entry.name}: {entry.value}
          </Text>
        </div>
      ))}
    </div>
  );
};

const LEGEND = [
  { key: 'lateCount', label: 'Late', color: 'var(--error-400)' },
  { key: 'onTimeCount', label: 'On-Time', color: 'var(--blue-400)' },
  { key: 'earlyCount', label: 'Early', color: 'var(--success-400)' },
];

export const CheckInTrendChart = ({ rollups, isLoading }) => {
  const data = rollups.map((r) => {
    // No real late/early data for this day (the login-alert trigger never
    // ran on it) means an empty bar, not a full "everyone was on time" bar —
    // presentCount comes from the separate, older productivity-tracking job
    // and exists for every day regardless of whether login alerts were ever
    // configured, so it must never be used to backfill on-time counts for a
    // day with no real breakdown. See the same note in StatsRow.component.jsx.
    if (!lateEarlyAlertsService.hasLoginAlertData(r)) {
      return {
        label: moment(r.dateId, 'YYYY-MM-DD').format('MMM D'),
        lateCount: 0,
        earlyCount: 0,
        onTimeCount: 0,
      };
    }
    const lateCount = Number(r.lateCount) || 0;
    const earlyCount = Number(r.earlyCount) || 0;
    const presentCount = Number(r.presentCount) || 0;
    return {
      label: moment(r.dateId, 'YYYY-MM-DD').format('MMM D'),
      lateCount,
      earlyCount,
      onTimeCount: Math.max(0, presentCount - lateCount - earlyCount),
    };
  });

  return (
    <div
      className={pageStyles.card}
      style={isLoading ? { opacity: 0.6 } : undefined}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardTitleGroup}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Semibold}
            color="gray-900"
          >
            Check-in Trend
          </Text>
          <InfoTooltip
            title="Check-in Trend"
            description="Employees who checked in late, on time, or early each day over the last 7 days."
          />
        </div>
        <div className={styles.legend}>
          {LEGEND.map((item) => (
            <span key={item.key} className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ background: item.color }}
              />
              {item.label}
            </span>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart
          data={data}
          margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
          barGap={4}
          barCategoryGap="35%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--gray-100)"
            vertical={false}
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: 'var(--gray-400)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--gray-400)' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'var(--gray-50)' }}
          />
          <Bar
            dataKey="lateCount"
            name="Late"
            stackId="checkins"
            fill="var(--error-400)"
            maxBarSize={32}
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="onTimeCount"
            name="On-Time"
            stackId="checkins"
            fill="var(--blue-400)"
            maxBarSize={32}
          />
          <Bar
            dataKey="earlyCount"
            name="Early"
            stackId="checkins"
            fill="var(--success-400)"
            maxBarSize={32}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
CheckInTrendChart.displayName = 'CheckInTrendChart';

export const CheckInTimeDistribution = ({ rollup, isLoading }) => {
  const histogram = rollup?.checkInHistogram || {};
  // Fixed bucket order (not object key order, which isn't guaranteed to
  // sort chronologically once PM buckets are involved).
  const BUCKET_ORDER = [
    '6 AM',
    '8 AM',
    '10 AM',
    '12 PM',
    '2 PM',
    '4 PM',
    '6 PM',
    '8 PM',
  ];
  const data = BUCKET_ORDER.map((bucket) => ({
    bucket,
    count: Number(histogram[bucket]) || 0,
  }));

  return (
    <div
      className={pageStyles.card}
      style={isLoading ? { opacity: 0.6 } : undefined}
    >
      <div className={styles.cardHeader}>
        <div className={styles.cardTitleGroup}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Semibold}
            color="gray-900"
          >
            Check-in Time Distribution
          </Text>
          <InfoTooltip
            title="Check-in Time Distribution"
            description="How many employees checked in during each 2-hour window today."
          />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart
          data={data}
          margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
          barCategoryGap="35%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--gray-100)"
            vertical={false}
          />
          <XAxis
            dataKey="bucket"
            tick={{ fontSize: 11, fill: 'var(--gray-400)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--gray-400)' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: 'var(--gray-50)' }}
          />
          <Bar
            dataKey="count"
            name="Employees"
            fill="var(--blue-300)"
            maxBarSize={40}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
CheckInTimeDistribution.displayName = 'CheckInTimeDistribution';
